import { ROUTES } from "#constants/routes"
import { UserSettings } from "#customTypes/globalTypes"
import { AttendanceStatus } from "#customTypes/types"
import { getNow, STANDARD_DATE_FORMAT } from "#utils/dateHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"

export const ATTENDANCE_STATUSES = {
  NOT_ENROLLED: "NOT_ENROLLED",

  ABSENT: "ABSENT",
  PRESENT: "PRESENT",
  TARDY: "TARDY",
  ENROLLED: "ENROLLED",

  PLANNED_ABSENCE: "PLANNED_ABSENCE",

  TRYOUT_PRESENT: "TRYOUT_PRESENT",
  TRYOUT_ABSENT: "TRYOUT_ABSENT",
  TRYOUT_TARDY: "TRYOUT_TARDY",
  TRYOUT_ENROLLED: "TRYOUT_ENROLLED",

  MAKEUP_PRESENT: "MAKEUP_PRESENT",
  MAKEUP_ABSENT: "MAKEUP_ABSENT",
  MAKEUP_TARDY: "MAKEUP_TARDY",
  MAKEUP_ENROLLED: "MAKEUP_ENROLLED",

  INVALID: "INVALID",
} as const

export const VALID_SWITCH_STATUSES: AttendanceStatus[] = [
  ATTENDANCE_STATUSES.NOT_ENROLLED,
  ATTENDANCE_STATUSES.ENROLLED,
  ATTENDANCE_STATUSES.INVALID,
] as const

// Attendance statuses that mean the student is expected (or was) at that session.
// Used to detect real time conflicts when offering makeup/tryout session options.
// Non-blocking because the student is not at that session at that time:
// - MAKEUP_*: status is written to the MISSED session; the student is making up
//   elsewhere. The destination session is tracked via the Makeup doc and handled
//   separately in session-option hooks.
// - PLANNED_ABSENCE: student is planned absent from that session.
// - TRYOUT_ABSENT: only set after the class is over, so the day has passed.
export const TIME_BLOCKING_ATTENDANCE_STATUSES: Record<
  AttendanceStatus,
  boolean
> = {
  [ATTENDANCE_STATUSES.ENROLLED]: true,
  [ATTENDANCE_STATUSES.PRESENT]: true,
  [ATTENDANCE_STATUSES.TARDY]: true,
  [ATTENDANCE_STATUSES.ABSENT]: true,

  [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: true,
  [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: true,
  [ATTENDANCE_STATUSES.TRYOUT_TARDY]: true,

  [ATTENDANCE_STATUSES.INVALID]: true,

  [ATTENDANCE_STATUSES.NOT_ENROLLED]: false,
  [ATTENDANCE_STATUSES.PLANNED_ABSENCE]: false,
  [ATTENDANCE_STATUSES.TRYOUT_ABSENT]: false,

  [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: false,
  [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: false,
  [ATTENDANCE_STATUSES.MAKEUP_TARDY]: false,
  [ATTENDANCE_STATUSES.MAKEUP_ABSENT]: false,
} as const

export const USER_SETTINGS = {
  THEME: "theme",
  LAST_PAGE: "lastPage",
  LOGGED_IN: "loggedIn",

  SELECTED_DATE: "selectedDate",

  // Per-table controlled sort, keyed by SETTINGS_KEYS value.
  TABLE_SORTS: "tableSorts",

  // Quality Check wall filters (scope global, class axes per level).
  QUALITY_CHECK_FILTERS: "qualityCheckFilters",

  // Standout Review dashboard filters (semester, grouping, class axes).
  STANDOUT_REVIEW_FILTERS: "standoutReviewFilters",

  // TeacherView personal-notes panel (collapsed by default).
  PERSONAL_NOTES_OPEN: "personalNotesOpen",

  CLASSES_TABLE_SEARCH_TERM: "classesTableSearchTerm",
  CLASSES_TABLE_SEMESTER: "classesTableSemester",
  CLASSES_TABLE_RESULTS_COUNT: "classesTableResultsCount",

  STUDENTS_TABLE_SEARCH_TERM: "studentsTableSearchTerm",
  // STUDENTS_TABLE_SEMESTER: "studentsTableSemester",
  STUDENTS_TABLE_RESULTS_COUNT: "studentsTableResultsCount",

  TRYOUTS_TABLE_SEARCH_TERM: "tryoutsTableSearchTerm",
  TRYOUTS_TABLE_SEMESTER: "tryoutsTableSemester",
  TRYOUTS_TABLE_RESULTS_COUNT: "tryoutsTableResultsCount",

  MAKEUPS_TABLE_SEARCH_TERM: "makeupsTableSearchTerm",
  MAKEUPS_TABLE_SEMESTER: "makeupsTableSemester",
  MAKEUPS_TABLE_RESULTS_COUNT: "makeupsTableResultsCount",

  CURRICULUMS_TABLE_SEARCH_TERM: "curriculumsTableSearchTerm",
  CURRICULUMS_TABLE_SEMESTER: "curriculumsTableSemester",
  CURRICULUMS_TABLE_RESULTS_COUNT: "curriculumsTableResultsCount",

  PROJECTS_TABLE_SEARCH_TERM: "projectsTableSearchTerm",
  PROJECTS_TABLE_SEMESTER: "projectsTableSemester",
  PROJECTS_TABLE_RESULTS_COUNT: "projectsTableResultsCount",

  USERS_TABLE_SEARCH_TERM: "usersTableSearchTerm",
  USERS_TABLE_RESULTS_COUNT: "usersTableResultsCount",
  USERS_TABLE_SHOW_DISABLED_USERS: "usersTableShowDisabledUsers",

  EVALUATIONS_TABLE_SEARCH_TERM: "evaluationsTableSearchTerm",
  EVALUATIONS_TABLE_RESULTS_COUNT: "evaluationsTableResultsCount",
  EVALUATIONS_TABLE_SEMESTER: "evaluationsTableSemester",
  EVALUATIONS_TABLE_STATUS_FILTER: "evaluationsTableStatusFilter",
  EVALUATIONS_TABLE_TEACHER_FILTER: "evaluationsTableTeacherFilter",
  EVALUATIONS_TABLE_APPROVER_FILTER: "evaluationsTableApproverFilter",
  EVALUATIONS_TABLE_PAGE: "evaluationsTablePage",
  EVALUATIONS_TABLE_SORT_BY_STATUS: "evaluationsTableSortByStatus",
} as const

export const DEFAULT_USER_SETTINGS: UserSettings = {
  [USER_SETTINGS.THEME]: "light",
  [USER_SETTINGS.LAST_PAGE]: ROUTES.FRONT_DESK_VIEW,
  [USER_SETTINGS.LOGGED_IN]: false,
  [USER_SETTINGS.SELECTED_DATE]: getNow().format(STANDARD_DATE_FORMAT),

  [USER_SETTINGS.TABLE_SORTS]: {},

  [USER_SETTINGS.QUALITY_CHECK_FILTERS]: {
    scope: "all",
    semester: getCurrentSemester(),
    curriculumId: null,
    weekday: null,
    teacherId: null,
    hideFinishedItems: true,
    hideFinishedClasses: false,
  },

  [USER_SETTINGS.STANDOUT_REVIEW_FILTERS]: {
    semester: getCurrentSemester(),
    groupBy: "student",
    teacherId: null,
    classId: null,
    category: null,
    curriculumId: null,
    hideFinished: true,
    hideFinishedClasses: false,
  },

  [USER_SETTINGS.PERSONAL_NOTES_OPEN]: false,

  [USER_SETTINGS.CLASSES_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.CLASSES_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.CLASSES_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.STUDENTS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.STUDENTS_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.TRYOUTS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.TRYOUTS_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.TRYOUTS_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.MAKEUPS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.MAKEUPS_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.MAKEUPS_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.CURRICULUMS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.CURRICULUMS_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.CURRICULUMS_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.PROJECTS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.PROJECTS_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.PROJECTS_TABLE_RESULTS_COUNT]: 20,

  [USER_SETTINGS.USERS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.USERS_TABLE_RESULTS_COUNT]: 50,
  [USER_SETTINGS.USERS_TABLE_SHOW_DISABLED_USERS]: false,

  [USER_SETTINGS.EVALUATIONS_TABLE_SEARCH_TERM]: "",
  [USER_SETTINGS.EVALUATIONS_TABLE_RESULTS_COUNT]: 5,
  [USER_SETTINGS.EVALUATIONS_TABLE_SEMESTER]: getCurrentSemester(),
  [USER_SETTINGS.EVALUATIONS_TABLE_STATUS_FILTER]: [],
  [USER_SETTINGS.EVALUATIONS_TABLE_TEACHER_FILTER]: [],
  [USER_SETTINGS.EVALUATIONS_TABLE_APPROVER_FILTER]: [],
  [USER_SETTINGS.EVALUATIONS_TABLE_PAGE]: 1,
  [USER_SETTINGS.EVALUATIONS_TABLE_SORT_BY_STATUS]: false,
}
